import styled from 'styled-components';
import { NoteValue } from '../../../../../common/src/rhythm_echo.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';

// The note and rest glyphs the rhythm rows draw (issue #3473), shared by the pattern notation
// (rhythm_notation.tsx) and the tap notation (rhythm_tap_notation.tsx) so a tap is drawn with
// exactly the glyph its expected note has. Each glyph is a fixed-size SVG in a 14 x 40 box on a
// single line, no staff: a note is a head on the baseline with a stem and, outside a beam, its
// flags; a rest is the rest shape for its value. Everything is currentColor so it follows the
// text colour in both themes and takes a result colour from its parent.

// ---- Geometry (px within a glyphWidth x rhythmNotationHeight glyph box) -------------------------

export const rhythmNotationHeight = 40;
export const glyphWidth = 14;
export const glyphInset = 6; // from the cell (and syllable) start to the glyph box
export const baseline = 32; // notehead centre
const headRx = 5.6;
const headRy = 3.8;
const headCx = 6;
export const stemX = 10.6; // the stem's left edge, on the head's right side
export const stemWidth = 1.4;
export const stemTop = 8;
export const beamThickness = 3;
export const beamSpacing = 5;

export function beamCount(value: NoteValue): number {
  return value === 'sixteenth' ? 2 : 1;
}

export function flagCount(value: NoteValue): number {
  switch (value) {
    case 'eighth':
      return 1;
    case 'sixteenth':
      return 2;
    default:
      return 0;
  }
}

export function noteGlyph(value: NoteValue, flags: number) {
  const open = value === 'whole' || value === 'half';
  const stem = value !== 'whole';
  return (
    <svg
      width={glyphWidth}
      height={rhythmNotationHeight}
      viewBox={`0 0 ${glyphWidth} ${rhythmNotationHeight}`}
      overflow="visible"
    >
      <ellipse
        cx={headCx}
        cy={baseline}
        rx={headRx}
        ry={headRy}
        transform={`rotate(-20 ${headCx} ${baseline})`}
        fill={open ? 'none' : 'currentColor'}
        stroke="currentColor"
        strokeWidth={open ? 1.6 : 0}
      />
      {stem && <rect x={stemX} y={stemTop} width={stemWidth} height={baseline - stemTop} fill="currentColor" />}
      {Array.from({ length: flags }, (_flag, index) => (
        <path key={index} d={flagPath(stemTop + index * beamSpacing)} fill="currentColor" />
      ))}
    </svg>
  );
}

// A flag hanging from the stem's right edge at `top`, for eighths and sixteenths outside a beam
function flagPath(top: number): string {
  const x = stemX + stemWidth;
  return `M${x} ${top} C${x} ${top + 7}, ${x + 6} ${top + 7}, ${x + 6} ${top + 14} C${x + 6} ${top + 9}, ${x + 4} ${top + 7}, ${x} ${top + 6} Z`;
}

export function restGlyph(value: NoteValue) {
  return (
    <svg
      width={glyphWidth}
      height={rhythmNotationHeight}
      viewBox={`0 0 ${glyphWidth} ${rhythmNotationHeight}`}
      overflow="visible"
    >
      {restShape(value)}
    </svg>
  );
}

function restShape(value: NoteValue) {
  switch (value) {
    case 'whole': // hangs below the line
      return <rect x={2} y={baseline} width={10} height={4} fill="currentColor" />;
    case 'half': // sits on the line
      return <rect x={2} y={baseline - 4} width={10} height={4} fill="currentColor" />;
    case 'quarter':
      return (
        <path
          d={`M4 ${baseline - 11} L10 ${baseline - 4} L5 ${baseline + 1} L10 ${baseline + 8} C6 ${baseline + 5}, 3 ${baseline + 6}, 6 ${baseline + 10}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case 'eighth':
      return (
        <>
          <circle cx={4} cy={baseline - 6} r={2} fill="currentColor" />
          <path
            d={`M4 ${baseline - 6} Q8 ${baseline - 2} 11 ${baseline - 7} L7 ${baseline + 7}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </>
      );
    case 'sixteenth':
      return (
        <>
          <circle cx={4} cy={baseline - 8} r={2} fill="currentColor" />
          <circle cx={3} cy={baseline - 2} r={2} fill="currentColor" />
          <path
            d={`M4 ${baseline - 8} Q8 ${baseline - 4} 11 ${baseline - 9} L6 ${baseline + 8}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
        </>
      );
    default:
      return ensureUnreachable(value);
  }
}

// ---- Styles -------------------------------------------------------------------------------------

// The single line the glyphs sit on, across the whole row
export const Baseline = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: ${baseline}px;
  height: 1px;
  background-color: currentColor;
  opacity: 0.3;
`;

// A glyph box, positioned absolutely by its parent
export const Glyph = styled.div`
  position: absolute;
  top: 0;
  width: ${glyphWidth}px;
  height: ${rhythmNotationHeight}px;
  line-height: 0;
`;
